import React from 'react';
import { FaStar, FaRegStar, FaStarHalfAlt } from 'react-icons/fa';

interface RatingProps {
    averageRating: number;
    reviewsNumber: number;
    /** When set, review count links here (e.g. TripAdvisor). Defaults to #reviews. */
    reviewsLinkHref?: string;
    /** When reviews come from TripAdvisor, adjusts link label. */
    reviewsSource?: 'internal' | 'tripadvisor';
}

const Rating: React.FC<RatingProps> = ({
    averageRating,
    reviewsNumber,
    reviewsLinkHref,
    reviewsSource = 'internal',
}) => {
    const href = reviewsLinkHref ?? '#reviews';
    const external = href.startsWith('http');
    const linkClass =
        'font-light text-neutral-500 text-sm ml-3 underline hover:text-cyan-700';

    const starsBlock = (
        <div className="flex items-center mr-2">
            {[...Array(5)].map((_, index) => {
                const isFull = index + 1 <= Math.floor(averageRating);
                const isHalf =
                    index + 1 === Math.ceil(averageRating) && averageRating % 1 !== 0;
                return (
                    <span key={index}>
                        {isFull ? (
                            <FaStar size={12} className="text-yellow-500" />
                        ) : isHalf ? (
                            <FaStarHalfAlt size={12} className="text-yellow-500" />
                        ) : (
                            <FaRegStar size={12} className="text-gray-300" />
                        )}
                    </span>
                );
            })}
        </div>
    );

    if (reviewsNumber === 0 && averageRating === 0) {
        return (
            <div className="flex items-center mt-1">
                <div className="flex items-center mr-2">
                    {[...Array(5)].map((_, index) => (
                        <span key={index}>
                            <FaStar size={12} className="text-gray-300" />
                        </span>
                    ))}
                    <div className="text-neutral-600 ml-2">No reviews yet</div>
                </div>
            </div>
        );
    }

    if (reviewsNumber === 0 && averageRating > 0) {
        return (
            <div className="flex items-center mt-1 flex-wrap gap-x-2">
                {starsBlock}
                <span className="text-md text-neutral-800">{averageRating.toFixed(1)}</span>
                {external && reviewsSource === 'tripadvisor' && (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClass}
                    >
                        TripAdvisor
                    </a>
                )}
            </div>
        );
    }

    const reviewsLabel =
        reviewsSource === 'tripadvisor'
            ? `${reviewsNumber} reviews on TripAdvisor`
            : `${reviewsNumber} reviews`;

    return (
        <div className="flex items-center mt-1">
            {starsBlock}
            <span className="text-md text-neutral-800">{averageRating.toFixed(1)}</span>
            <div className={linkClass}>
                <a
                    href={href}
                    {...(external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                >
                    {reviewsLabel}
                </a>
            </div>
        </div>
    );
};

export default Rating;
